import { useEffect } from "react";
import { APIProvider, Map, AdvancedMarker, Pin, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useMapsConfig } from "@/lib/maps";
import { MapPinOff } from "lucide-react";

const CENTER = { lat: 39.99, lng: -82.88 };

const RouteLine = ({ encoded }) => {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  useEffect(() => {
    if (!map || !geometry || !encoded) return;
    const path = geometry.encoding.decodePath(encoded);
    const line = new window.google.maps.Polyline({ path, strokeColor: "#C8202F", strokeWeight: 4, map });
    const b = new window.google.maps.LatLngBounds(); path.forEach((p) => b.extend(p)); map.fitBounds(b, 60);
    return () => line.setMap(null);
  }, [map, geometry, encoded]);
  return null;
};

const FitPoints = ({ points }) => {
  const map = useMap();
  const sig = points.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (!map || !points.length || !window.google) return;
    if (points.length === 1) { map.setCenter(points[0]); map.setZoom(13); return; }
    const b = new window.google.maps.LatLngBounds(); points.forEach((p) => b.extend(p)); map.fitBounds(b, 60);
  }, [map, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// markers: [{id, lat, lng, label, color, title}]
export const FleetMap = ({ markers = [], polyline, height = 420, testid = "fleet-live-map" }) => {
  const cfg = useMapsConfig();
  if (!cfg) return <div className="rounded-lg bg-muted animate-pulse" style={{ height }} />;
  if (!cfg.map_enabled) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/40 flex flex-col items-center justify-center text-center p-6 text-sm text-muted-foreground" style={{ height: Math.min(height, 200) }} data-testid="map-not-configured">
        <MapPinOff className="w-6 h-6 mb-2" />
        Live map not configured. A Super Admin can add a Google Maps browser key in Settings → Maps & Routing.
      </div>
    );
  }
  const valid = markers.filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height }} data-testid={testid}>
      <APIProvider apiKey={cfg.browser_key}>
        <Map defaultCenter={CENTER} defaultZoom={10} mapId={cfg.map_id} gestureHandling="greedy" disableDefaultUI zoomControl style={{ width: "100%", height: "100%" }}>
          {valid.map((m) => (
            <AdvancedMarker key={m.id} position={{ lat: m.lat, lng: m.lng }} title={m.title}>
              <Pin background={m.color || "#C8202F"} borderColor="#141414" glyphColor="#fff" glyph={m.label} />
            </AdvancedMarker>
          ))}
          {polyline ? <RouteLine encoded={polyline} /> : <FitPoints points={valid} />}
        </Map>
      </APIProvider>
    </div>
  );
};
